// React
import { useCallback } from 'react'

// Models
import { useStores } from 'models'

// Interfaces
import {
  IAssessmentAnotherDocumentGetListAttrs,
  IAssessmentAnotherDocumentCreateAttrs,
  IAssessmentAnotherDocumentGetDetailAttrs,
  IAssessmentAnotherDocumentUpdateAttrs,
  IAssessmentAnotherDocumentDeleteAttrs,
  IAssessmentAnotherDocumentDownloadDocumentAttrs,
  IAssessmentAnotherDocumentUploadDocumentAttrs,
} from 'interfaces/back-office/assessment/another-document/another-document-view.interface'

const useAssessmentAnotherDocument = () => {
  // Hook
  const {
    assessmentAnotherDocument: {
      assessmentAnotherDocument_loading,
      assessmentAnotherDocument_list,
      assessmentAnotherDocument_detail,
      assessmentAnotherDocument_CLEAR_LIST,
      assessmentAnotherDocument_CLEAR_DETAIL,
      assessmentAnotherDocument_MAP_USER_FILE_DETAIL,
      assessmentAnotherDocument_fetchList: fetchList,
      assessmentAnotherDocument_create: create,
      assessmentAnotherDocument_fetchDetail: fetchDetail,
      assessmentAnotherDocument_update: update,
      assessmentAnotherDocument_delete: _delete,
      assessmentAnotherDocument_uploadDocument: uploadDocument,
      assessmentAnotherDocument_downloadDocument: downloadDocument,
      assessmentAnotherDocument_document_name_list
    },
  } = useStores()

  /**
   * @description Fetch assessment another document list
   *
   * @param {IAssessmentAnotherDocumentGetListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentAnotherDocument_fetchList = useCallback(
    (payload: IAssessmentAnotherDocumentGetListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchList({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchList],
  )

  /**
   * @description Create assessment another document
   *
   * @param {IAssessmentAnotherDocumentCreateAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentAnotherDocument_create = useCallback(
    (payload: IAssessmentAnotherDocumentCreateAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        create({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [create],
  )

  /**
   * @description Get assessment another document detail
   *
   * @param {IAssessmentAnotherDocumentGetDetailAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentAnotherDocument_fetchDetail = useCallback(
    (payload: IAssessmentAnotherDocumentGetDetailAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDetail({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchDetail],
  )

  /**
   * @description Update assessment another document
   *
   * @param {IAssessmentAnotherDocumentUpdateAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentAnotherDocument_update = useCallback(
    (payload: IAssessmentAnotherDocumentUpdateAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        update({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [update],
  )

  /**
   * @description Delete assessment another document
   *
   * @param {IAssessmentAnotherDocumentDeleteAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentAnotherDocument_delete = useCallback(
    (payload: IAssessmentAnotherDocumentDeleteAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        _delete({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [_delete],
  )

  /**
   * @description Upload document of another document
   *
   * @param {IAssessmentAnotherDocumentUploadDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentAnotherDocument_uploadDocument = useCallback(
    (payload: IAssessmentAnotherDocumentUploadDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        uploadDocument({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [uploadDocument],
  )

  /**
   * @description Download assessment another document, document
   *
   * @param {IAssessmentAnotherDocumentDownloadDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentAnotherDocument_downloadDocument = useCallback(
    (payload: IAssessmentAnotherDocumentDownloadDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        downloadDocument({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [downloadDocument],
  )

  return {
    assessmentAnotherDocument_loading,
    assessmentAnotherDocument_list,
    assessmentAnotherDocument_detail,
    assessmentAnotherDocument_document_name_list,
    assessmentAnotherDocument_CLEAR_LIST,
    assessmentAnotherDocument_CLEAR_DETAIL,
    assessmentAnotherDocument_MAP_USER_FILE_DETAIL,
    assessmentAnotherDocument_fetchList,
    assessmentAnotherDocument_create,
    assessmentAnotherDocument_fetchDetail,
    assessmentAnotherDocument_update,
    assessmentAnotherDocument_delete,
    assessmentAnotherDocument_uploadDocument,
    assessmentAnotherDocument_downloadDocument,
  }
}

export { useAssessmentAnotherDocument }
